import { createFileRoute } from '@tanstack/react-router';
import {
    BookOpen,
    CheckCircle2,
    Clock,
    type LucideIcon,
    Search,
    Settings,
    Wrench,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { AgentModelPicker } from '@apps/web/components/agent_model_picker';
import { RoleBadge } from '@apps/web/components/caregivers/role_badge';
import { ContactAttemptStatusBadge } from '@apps/web/components/outreaches/contact_attempt_status_badge';
import { OutreachStatusBadge } from '@apps/web/components/outreaches/outreach_status_badge';
import { ChatInput } from '@apps/web/components/phoebe_chat/chat_input';
import {
    LiveWorkTraceCard,
    type LiveWorkTraceItem,
} from '@apps/web/components/phoebe_chat/live_work_trace_card';
import {
    MonitoringConfigurationCard,
    type MonitoringStreamRunNode,
} from '@apps/web/components/phoebe_chat/monitoring_configuration_card';
import { type ToolApprovalGroup } from '@apps/web/components/phoebe_chat/transcript_types';
import { AssistantResponseContent } from '@apps/web/components/phoebe_chat_view/assistant_responses/assistant_response_content';
import { ToolApprovalGroupCard } from '@apps/web/components/phoebe_chat_view/tool_calls/grouped_tool_approval_card';
import {
    AvailabilityActionCard,
    MemoryActionCard,
    RecordUpdateActionCard,
} from '@apps/web/components/phoebe_chat_view/tool_calls/record_settings_tool_ui';
import { SkillContextToolCard } from '@apps/web/components/phoebe_chat_view/tool_calls/skill_context_tool_ui';
import {
    GenericToolResultCard,
    ToolExecutionCardShell,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import { Badge } from '@apps/web/components/ui/badge';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';

const LIVE_WORK_TRACE_ITEMS = [
    {
        id: 'intake',
        title: 'Understand the request',
        status: 'completed',
        subtitle: 'Parsed the selected organization and run context',
        tools: [
            {
                id: 'context',
                label: 'Organization context',
                status: 'completed',
                summary: 'Phoebe Home Care, sandbox mode',
                icon: Search,
            },
        ],
    },
    {
        id: 'settings',
        title: 'Prepare settings update',
        status: 'running',
        subtitle: 'Waiting on approval before writing changes',
        tools: [
            {
                id: 'approval',
                label: 'Update recommendation defaults',
                status: 'running',
                summary: '2 fields staged',
                icon: Settings,
            },
        ],
    },
    {
        id: 'finish',
        title: 'Send summary',
        status: 'pending',
        subtitle: 'Summarize changes after tools complete',
    },
] satisfies ReadonlyArray<LiveWorkTraceItem>;

const MONITORING_TOOL_NODES = [
    {
        kind: 'tool',
        toolName: 'monitor_outreach',
        arguments: {
            conditions: [
                'Add caregivers when no caregiver responds for 30 minutes',
                'Notify me when someone accepts',
            ],
        },
        result: {
            status: 'monitoring',
        },
    },
] satisfies ReadonlyArray<MonitoringStreamRunNode>;

const ASSISTANT_RESPONSE_CONTENT = [
    'I can update that for Phoebe Home Care.',
    '',
    '- Teach Phoebe that coordinators prefer last-name-first caregiver lists.',
    '- Stage the Settings change for approval before anything writes.',
    '- Monitor the rollout and report back when it is complete.',
].join('\n');

const SETTINGS_APPROVAL_GROUP = {
    id: 'approval-settings',
    kind: 'settings',
    toolName: 'update_organization_general_settings',
    title: 'Approve organization settings updates',
    mode: 'sandbox',
    targetCount: 2,
    callIds: ['call-1', 'call-2'],
    commandIds: ['command-1', 'command-2'],
    featureValues: {
        enabled: true,
    },
    targets: [
        {
            commandId: 'command-1',
            callId: 'call-1',
            organizationId: 'org-1',
            organizationName: 'Phoebe Home Care',
            organizationInternalName: 'phoebe-home-care',
            displayName: 'Phoebe Home Care',
            changes: [
                {
                    key: 'client_reference_term',
                    before: 'patient',
                    after: 'client',
                    changed: true,
                },
            ],
        },
        {
            commandId: 'command-2',
            callId: 'call-2',
            organizationId: 'org-2',
            organizationName: 'Garden Home Health',
            organizationInternalName: 'garden-home-health',
            displayName: 'Garden Home Health',
            changes: [
                {
                    key: 'display_caregiver_names_last_first',
                    before: false,
                    after: true,
                    changed: true,
                },
            ],
        },
    ],
    riskSummary:
        'Changes affect how names and client terminology appear in coordinator workflows.',
} satisfies ToolApprovalGroup;

export const Route = createFileRoute('/_app/admin/ui-components')({
    component: UIComponentsShowcase,
    head: () => ({
        meta: [
            {
                title: 'UI Components | Phoebe Admin',
            },
        ],
    }),
});

function UIComponentsShowcase() {
    return (
        <div className="container mx-auto space-y-8 p-6">
            <div>
                <h1 className="mb-2 text-3xl font-bold">
                    UI Components Showcase
                </h1>
                <p className="text-muted-foreground">
                    A comprehensive display of all UI components and their
                    variations
                </p>
            </div>
            <ChatComponentsShowcase />
            {/* Badge Component Variants */}
            <Card>
                <CardHeader>
                    <CardTitle>Badge Component</CardTitle>
                    <CardDescription>
                        Basic badge component with different variants
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap gap-2">
                        <Badge>Default</Badge>
                        <Badge variant="secondary">Secondary</Badge>
                        <Badge variant="destructive">Destructive</Badge>
                        <Badge variant="outline">Outline</Badge>
                    </div>
                </CardContent>
            </Card>
            {/* Open Shift Status Badges */}
            <Card>
                <CardHeader>
                    <CardTitle>Open Shift Status Badges</CardTitle>
                    <CardDescription>
                        Status badges with colored dot indicators for open
                        shifts
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Status</TableHead>
                                <TableHead>Badge</TableHead>
                                <TableHead>Description</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    pending
                                </TableCell>
                                <TableCell>
                                    <OutreachStatusBadge status="pending" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Initial state, no outreach started
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    in_progress
                                </TableCell>
                                <TableCell>
                                    <OutreachStatusBadge status="in_progress" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Actively contacting caregivers
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    awaiting_confirmation
                                </TableCell>
                                <TableCell>
                                    <OutreachStatusBadge status="awaiting_confirmation" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Waiting for caregiver response
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    filled
                                </TableCell>
                                <TableCell>
                                    <OutreachStatusBadge status="filled" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Successfully filled with replacement
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    unfilled
                                </TableCell>
                                <TableCell>
                                    <OutreachStatusBadge status="unfilled" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Could not find replacement
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    archived
                                </TableCell>
                                <TableCell>
                                    <OutreachStatusBadge status="cancelled" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Outreach was archived
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            {/* Contact Attempt Status Badges */}
            <Card>
                <CardHeader>
                    <CardTitle>Contact Attempt Status Badges</CardTitle>
                    <CardDescription>
                        Status badges with colored dot indicators for contact
                        attempts
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Status</TableHead>
                                <TableHead>Badge</TableHead>
                                <TableHead>Description</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {/* In Progress States */}
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    queued
                                </TableCell>
                                <TableCell>
                                    <ContactAttemptStatusBadge status="queued" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Awaiting dispatch to the delivery provider
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    initiated
                                </TableCell>
                                <TableCell>
                                    <ContactAttemptStatusBadge status="initiated" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Contact attempt has been initiated
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    sent
                                </TableCell>
                                <TableCell>
                                    <ContactAttemptStatusBadge status="sent" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Message or call has been sent
                                </TableCell>
                            </TableRow>
                            {/* Successful States */}
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    delivered
                                </TableCell>
                                <TableCell>
                                    <ContactAttemptStatusBadge status="delivered" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Message was delivered successfully
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    completed
                                </TableCell>
                                <TableCell>
                                    <ContactAttemptStatusBadge status="completed" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Contact attempt completed successfully
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    live_answer
                                </TableCell>
                                <TableCell>
                                    <ContactAttemptStatusBadge status="live_answer" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Call was answered by a person
                                </TableCell>
                            </TableRow>
                            {/* Failed/Unsuccessful States */}
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    failed
                                </TableCell>
                                <TableCell>
                                    <ContactAttemptStatusBadge status="failed" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Contact attempt failed
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    no_answer
                                </TableCell>
                                <TableCell>
                                    <ContactAttemptStatusBadge status="no_answer" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Call was not answered
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell className="font-mono text-sm">
                                    voicemail_left
                                </TableCell>
                                <TableCell>
                                    <ContactAttemptStatusBadge status="voicemail_left" />
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    Voicemail message was left
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            {/* Role Badges */}
            <Card>
                <CardHeader>
                    <CardTitle>Caregiver Role Badges</CardTitle>
                    <CardDescription>
                        Role badges with healthcare-specific color coding
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div>
                            <h4 className="mb-2 text-sm font-medium">
                                Abbreviated Format
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                <RoleBadge role="rn" />
                                <RoleBadge role="lpn" />
                                <RoleBadge role="cna" />
                                <RoleBadge role="hha" />
                                <RoleBadge role="pt" />
                                <RoleBadge role="ot" />
                                <RoleBadge role="st" />
                                <RoleBadge role="msw" />
                                <RoleBadge role="cpi" />
                                <RoleBadge role="dsw" />
                                <RoleBadge role="np" />
                                <RoleBadge role="other" />
                            </div>
                        </div>
                        <div>
                            <h4 className="mb-2 text-sm font-medium">
                                Full Name Format
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                <RoleBadge role="rn" showFullName={true} />
                                <RoleBadge role="lpn" showFullName={true} />
                                <RoleBadge role="cna" showFullName={true} />
                                <RoleBadge role="hha" showFullName={true} />
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
            {/* Role Badge Reference Table */}
            <Card>
                <CardHeader>
                    <CardTitle>Role Badge Reference</CardTitle>
                    <CardDescription>
                        Complete mapping of role abbreviations to full names
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Role Code</TableHead>
                                <TableHead>Badge</TableHead>
                                <TableHead>Full Name</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {[
                                {
                                    code: 'rn' as const,
                                    name: 'Registered Nurse',
                                },
                                {
                                    code: 'lpn' as const,
                                    name: 'Licensed Practical Nurse',
                                },
                                {
                                    code: 'cna' as const,
                                    name: 'Certified Nursing Assistant',
                                },
                                {
                                    code: 'hha' as const,
                                    name: 'Home Health Aide',
                                },
                                {
                                    code: 'pt' as const,
                                    name: 'Physical Therapist',
                                },
                                {
                                    code: 'ot' as const,
                                    name: 'Occupational Therapist',
                                },
                                {
                                    code: 'st' as const,
                                    name: 'Speech Therapist',
                                },
                                {
                                    code: 'msw' as const,
                                    name: 'Medical Social Worker',
                                },
                                {
                                    code: 'cpi' as const,
                                    name: 'Certified Patient Instructor',
                                },
                                {
                                    code: 'dsw' as const,
                                    name: 'Direct Support Worker',
                                },
                                {
                                    code: 'np' as const,
                                    name: 'Nurse Practitioner',
                                },
                                { code: 'other' as const, name: 'Other' },
                            ].map(({ code, name }) => (
                                <TableRow key={code}>
                                    <TableCell className="font-mono text-sm">
                                        {code}
                                    </TableCell>
                                    <TableCell>
                                        <RoleBadge role={code} />
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {name}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            {/* Color Palette */}
            <Card>
                <CardHeader>
                    <CardTitle>Component Color Palette</CardTitle>
                    <CardDescription>
                        Colors used across different component states
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                        <div className="space-y-2">
                            <div className="h-16 rounded border border-gray-200 bg-gray-100" />
                            <p className="text-sm font-medium">
                                Gray (Pending/Other)
                            </p>
                            <p className="text-muted-foreground text-xs">
                                bg-gray-100
                            </p>
                        </div>
                        <div className="space-y-2">
                            <div className="h-16 rounded border border-blue-200 bg-blue-100" />
                            <p className="text-sm font-medium">
                                Blue (In Progress/RN)
                            </p>
                            <p className="text-muted-foreground text-xs">
                                bg-blue-100
                            </p>
                        </div>
                        <div className="space-y-2">
                            <div className="h-16 rounded border border-orange-200 bg-orange-100" />
                            <p className="text-sm font-medium">
                                Orange (Awaiting/MSW)
                            </p>
                            <p className="text-muted-foreground text-xs">
                                bg-orange-100
                            </p>
                        </div>
                        <div className="space-y-2">
                            <div className="h-16 rounded border border-green-200 bg-green-100" />
                            <p className="text-sm font-medium">
                                Green (Success/PT)
                            </p>
                            <p className="text-muted-foreground text-xs">
                                bg-green-100
                            </p>
                        </div>
                        <div className="space-y-2">
                            <div className="h-16 rounded border border-red-200 bg-red-100" />
                            <p className="text-sm font-medium">
                                Red (Error/Unfilled)
                            </p>
                            <p className="text-muted-foreground text-xs">
                                bg-red-100
                            </p>
                        </div>
                        <div className="space-y-2">
                            <div className="h-16 rounded border border-purple-200 bg-purple-100" />
                            <p className="text-sm font-medium">Purple (CNA)</p>
                            <p className="text-muted-foreground text-xs">
                                bg-purple-100
                            </p>
                        </div>
                        <div className="space-y-2">
                            <div className="h-16 rounded border border-indigo-200 bg-indigo-100" />
                            <p className="text-sm font-medium">Indigo (LPN)</p>
                            <p className="text-muted-foreground text-xs">
                                bg-indigo-100
                            </p>
                        </div>
                        <div className="space-y-2">
                            <div className="h-16 rounded border border-violet-200 bg-violet-100" />
                            <p className="text-sm font-medium">Violet (HHA)</p>
                            <p className="text-muted-foreground text-xs">
                                bg-violet-100
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

function ChatComponentsShowcase() {
    const [activeModel, setActiveModel] = useState<string | null>(null);
    const [agentMode, setAgentMode] = useState<'live' | 'sandbox'>('sandbox');
    return (
        <Card>
            <CardHeader>
                <CardTitle>Chat Components</CardTitle>
                <CardDescription>
                    Shared Phoebe chat surfaces and tool-result cards with
                    fixture data
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
                    <ChatExamplePanel title="Composer">
                        <div className="space-y-3">
                            <div className="flex flex-wrap items-center gap-3">
                                <AgentModelPicker
                                    pinnedModel={activeModel}
                                    resolvedModel={activeModel}
                                    options={undefined}
                                    onModelChange={setActiveModel}
                                />
                                <div className="bg-background rounded-full border p-1">
                                    {(['sandbox', 'live'] as const).map(
                                        (mode) => (
                                            <button
                                                key={mode}
                                                type="button"
                                                onClick={() =>
                                                    setAgentMode(mode)
                                                }
                                                className={
                                                    agentMode === mode
                                                        ? 'bg-primary text-primary-foreground rounded-full px-3 py-1 text-xs font-medium'
                                                        : 'text-muted-foreground rounded-full px-3 py-1 text-xs font-medium'
                                                }
                                            >
                                                {mode}
                                            </button>
                                        ),
                                    )}
                                </div>
                            </div>
                            <ChatInput
                                onSubmit={() => undefined}
                                disabled={false}
                                isAgentBusy={false}
                                isStopping={false}
                                onStopRun={() => undefined}
                                placeholder="Ask Phoebe to teach a preference or update settings"
                                organizationId={null}
                                canRecordAudio={true}
                                isRecording={false}
                                isTranscribing={false}
                                isVoiceInputDisabled={false}
                                onStartRecording={() => undefined}
                                onStopRecording={() => undefined}
                                imageAttachments={[
                                    {
                                        id: 'image-1',
                                        filename: 'care-plan.png',
                                        mimeType: 'image/png',
                                        sizeBytes: 284_000,
                                        previewUrl: null,
                                        status: 'ready',
                                    },
                                ]}
                                onImageFilesSelected={() => undefined}
                                onRemoveImageAttachment={() => undefined}
                            />
                        </div>
                    </ChatExamplePanel>
                    <ChatExamplePanel title="Live Work">
                        <LiveWorkTraceCard
                            items={LIVE_WORK_TRACE_ITEMS}
                            title="Phoebe work"
                            statusLabel="Running"
                            runStatus="running"
                            className="max-w-none"
                        />
                    </ChatExamplePanel>
                </div>
                <div className="grid gap-4 xl:grid-cols-2">
                    <ChatExamplePanel title="Assistant Response">
                        <div className="bg-background rounded-lg border p-4">
                            <AssistantResponseContent
                                content={ASSISTANT_RESPONSE_CONTENT}
                            />
                        </div>
                    </ChatExamplePanel>
                    <ChatExamplePanel title="Monitoring Configuration">
                        <MonitoringConfigurationCard
                            tools={MONITORING_TOOL_NODES}
                            controls={{
                                isStopping: false,
                                isConfiguring: false,
                                onStop: () => undefined,
                                onConfigure: () => Promise.resolve(true),
                            }}
                            className="max-w-none"
                        />
                    </ChatExamplePanel>
                </div>
                <ChatExamplePanel title="Teach Phoebe">
                    <div className="grid gap-3 xl:grid-cols-2">
                        <ToolExecutionPreview
                            title="Checked available skills"
                            toolName="view_skills"
                            icon={BookOpen}
                            detail="3 available, 1 loaded"
                        >
                            <SkillContextToolCard
                                toolName="view_skills"
                                output={{
                                    skills: [
                                        {
                                            name: 'teach_phoebe',
                                            skill_display_name: 'Teach Phoebe',
                                            description:
                                                'Save durable agency instructions from chat.',
                                            loaded: true,
                                            requires_user_confirmation: false,
                                            skill_variant: 'early_access',
                                        },
                                        {
                                            name: 'settings',
                                            skill_display_name: 'Settings',
                                            description:
                                                'Read and stage organization settings changes.',
                                            loaded: false,
                                            requires_user_confirmation: true,
                                        },
                                        {
                                            name: 'outreach',
                                            skill_display_name: 'Outreach',
                                            description:
                                                'Inspect outreach state and caregiver responses.',
                                            loaded: false,
                                            requires_user_confirmation: false,
                                        },
                                    ],
                                }}
                            />
                        </ToolExecutionPreview>
                        <ToolExecutionPreview
                            title="Loaded Teach Phoebe skill"
                            toolName="load_skill"
                            icon={BookOpen}
                            detail="Teach Phoebe context"
                        >
                            <SkillContextToolCard
                                toolName="load_skill"
                                args={{ skill_name: 'teach_phoebe' }}
                                output={{
                                    skill_name: 'teach_phoebe',
                                    skill_display_name: 'Teach Phoebe',
                                    skill_variant: 'early_access',
                                }}
                            />
                        </ToolExecutionPreview>
                    </div>
                </ChatExamplePanel>
                <ChatExamplePanel title="Settings And Memory Tools">
                    <div className="grid gap-3 xl:grid-cols-3">
                        <ToolExecutionPreview
                            title="Updated caregiver record"
                            toolName="update_caregiver_record"
                            icon={Settings}
                            detail="Language and office fields changed"
                        >
                            <RecordUpdateActionCard
                                toolName="update_caregiver_record"
                                data={{
                                    caregiver_name: 'Rosa Lee',
                                    updated_fields: [
                                        'preferred_language',
                                        'office_ids',
                                    ],
                                }}
                                args={null}
                            />
                        </ToolExecutionPreview>
                        <ToolExecutionPreview
                            title="Added weekly availability"
                            toolName="add_caregiver_weekly_availability"
                            icon={Clock}
                            detail="Monday 8:00 AM - 4:00 PM"
                        >
                            <AvailabilityActionCard
                                toolName="add_caregiver_weekly_availability"
                                data={{
                                    caregiver_name: 'Rosa Lee',
                                    availability_id: 'availability-1',
                                }}
                                args={{
                                    day: 'monday',
                                    start_time: '08:00',
                                    end_time: '16:00',
                                }}
                            />
                        </ToolExecutionPreview>
                        <ToolExecutionPreview
                            title="Saved caregiver instruction"
                            toolName="save_caregiver_memory_instruction"
                            icon={CheckCircle2}
                            detail="Instruction saved"
                        >
                            <MemoryActionCard
                                toolName="save_caregiver_memory_instruction"
                                data={{
                                    caregiver_name: 'Rosa Lee',
                                    instruction_preview:
                                        'Offer weekend shifts by text before calling.',
                                    memory_id: 'memory-1',
                                    version: 3,
                                    character_count: 49,
                                }}
                                args={null}
                            />
                        </ToolExecutionPreview>
                    </div>
                </ChatExamplePanel>
                <div className="grid gap-4 xl:grid-cols-2">
                    <ChatExamplePanel title="Settings Approval">
                        <ToolApprovalGroupCard
                            group={SETTINGS_APPROVAL_GROUP}
                            isSubmitting={false}
                            onDecision={async () => undefined}
                            approvalPosition={1}
                            approvalCount={2}
                            canGoToNextApproval={true}
                            onNextApproval={() => undefined}
                        />
                    </ChatExamplePanel>
                    <ChatExamplePanel title="Generic Tool Result">
                        <ToolExecutionPreview
                            title="Searched conversation history"
                            toolName="search_conversation_history"
                            icon={Wrench}
                            detail="2 matching conversations"
                        >
                            <GenericToolResultCard
                                emptyText="No conversations found"
                                value={{
                                    summary:
                                        'Found matching caregiver availability instructions.',
                                    results: [
                                        {
                                            caregiver: 'Rosa Lee',
                                            source: 'Teach Phoebe',
                                            match: 'Prefers weekend shifts by text.',
                                        },
                                        {
                                            caregiver: 'Jules Rivera',
                                            source: 'Settings',
                                            match: 'Office assignment updated last week.',
                                        },
                                    ],
                                }}
                            />
                        </ToolExecutionPreview>
                    </ChatExamplePanel>
                </div>
            </CardContent>
        </Card>
    );
}

function ChatExamplePanel({
    title,
    children,
}: {
    readonly title: string;
    readonly children: ReactNode;
}) {
    return (
        <section className="space-y-3">
            <h3 className="text-sm font-semibold">{title}</h3>
            {children}
        </section>
    );
}

function ToolExecutionPreview({
    title,
    toolName,
    icon,
    detail,
    children,
}: {
    readonly title: string;
    readonly toolName: string;
    readonly icon: LucideIcon;
    readonly detail: string;
    readonly children: ReactNode;
}) {
    const [isOpen, setIsOpen] = useState(true);
    return (
        <ToolExecutionCardShell
            title={title}
            toolName={toolName}
            icon={icon}
            status="completed"
            durationLabel="1.2s"
            approvalBadge={null}
            detail={detail}
            isOpen={isOpen}
            onToggle={() => setIsOpen((current) => !current)}
            nested={true}
        >
            {children}
        </ToolExecutionCardShell>
    );
}
